import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useEffect, useState } from 'react';
import { SepOption } from './WorkflowValidation';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, useTheme } from '@mui/material';
import { useGetMultipleSEPsTasksQuery, useGetTaskTemplatesQuery } from '../../../../../frontend/services/tasksSlice/tasksSlice';

interface WorkflowValidationTableProps {
  selectedSeps: SepOption[];
}

const getTaskAdditionalDependencies = (taskDependencies: number[], templateTaskDependencies: number[]) => {
  const info: { taskAdditionalDependencies: number[]; templateAdditionalDependencies: number[], isDifferent: boolean } = {
    taskAdditionalDependencies: [],
    templateAdditionalDependencies: [],
    isDifferent: false
  };

  taskDependencies.forEach(id => {
    if (!templateTaskDependencies.includes(id)) {
      info.taskAdditionalDependencies.push(id);
    }
  });

  templateTaskDependencies.forEach(id => {
    if (!taskDependencies.includes(id)) {
      info.templateAdditionalDependencies.push(id);
    }
  });

  info.isDifferent = info.taskAdditionalDependencies.length > 0 || info.templateAdditionalDependencies.length > 0;

  return info;
};



const WorkflowValidationTable: React.FC<WorkflowValidationTableProps> = ({ selectedSeps }) => {
  const theme = useTheme();
  const { data: sepTasks, isLoading } = useGetMultipleSEPsTasksQuery(selectedSeps.map(sep => sep.id));
  const [templateTasks, setTemplateTasks] = useState<any[]>([]);
  const [sepObjects, setSepObjects] = useState<any[]>([]);

  useEffect(() => {
    if (Array.isArray(sepTasks)) {
      const tasks = sepTasks.filter(task => !task.template);
      setTemplateTasks(sepTasks.filter(task => task.template));
      // Prepare SEP objects with enabled tasks
      const sepObjects = selectedSeps.map(sep => {
        const enabledTasks = tasks.filter(task => task.sepID === sep.id);
        return { ...sep, enabledTasks };
      });
      setSepObjects(sepObjects);
    }
  }, [sepTasks]);

  // Show loading while fetching tasks
  if (isLoading) {
    return (
      <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.xl, mt: 4, textAlign: 'center', color: theme.palette.primary.main }}>
        Loading SEP tasks and templates...
      </Typography>
    );
  }


  // Collect all differences for each SEP and task
  const rows: Array<{
    sepName: string;
    taskName: string;
    templateValue?: string;
    differences: { field: string; sepValue: any; templateValue: any }[];
  }> = [];

  sepObjects.forEach(sepObj => {
    const clonedTasks = sepObj.enabledTasks.map((task: any) => ({ ...task }));
    clonedTasks.forEach((task: any) => {
      task.precedingTaskIds = Array.isArray(task.precedingTaskIDs) ? task.precedingTaskIDs : [];
      task.followingTaskIds = Array.isArray(task.followingTaskIDs) ? task.followingTaskIDs : [];
    });
    clonedTasks.forEach((task: any) => {
      const template = templateTasks.find(t => t.id === task.taskTemplateID);
      const diffs: { field: string; sepValue: any; templateValue: any }[] = [];
      if (!template) {
        // If no template, show Custom Task in SEP Value column
        diffs.push({ field: 'Custom Task', sepValue: 'Custom Task', templateValue: '' });
        rows.push({
          sepName: sepObj.name,
          taskName: task.name,
          differences: diffs,
        });
        return;
      }
      // Compare name and phase
      if (task.name !== template.name)
        diffs.push({ field: 'Name', sepValue: task.name, templateValue: template.name });
      if (task.phase !== template.phase)
        diffs.push({ field: 'Phase', sepValue: task.phase, templateValue: template.phase });
      // Build template dependency arrays by mapping template dependencies to SEP taskTemplateID
      // For SEP task's precedingTaskIds, find template tasks whose followingTaskIds include this task's taskTemplateID
      const taskPrecedingIds = task.precedingTaskIds.filter((id: any) => {
        const tempTask = clonedTasks.find((t: any) => t.id === id && t.enabled);
        return tempTask;
      });
      const templatePrecedingIds = template.precedingTaskIDs.map((tt: any) => {
        const tempTask = clonedTasks.find((t: any) => t.taskTemplateID === tt && t.enabled);
        return tempTask ? tempTask.id : null;
      }).filter((id: any) => id !== null);
      // For SEP task's followingTaskIds, find template tasks whose precedingTaskIds include this task's taskTemplateID
      const taskFollowingIds = task.followingTaskIds.filter((id: any) => {
        const tempTask = clonedTasks.find((t: any) => t.id === id && t.enabled);
        return tempTask;
      });
      const templateFollowingIds = template.followingTaskIDs.map((tt: any) => {
        const tempTask = clonedTasks.find((t: any) => t.taskTemplateID === tt && t.enabled);
        return tempTask ? tempTask.id : null;
      }).filter((id: any) => id !== null);
      // Only show differences for precedingTaskIds and followingTaskIds
      const dependencies = getTaskAdditionalDependencies(taskPrecedingIds, templatePrecedingIds);
      if (dependencies.isDifferent) {
        const taskList = dependencies.taskAdditionalDependencies.map((id) => {
          const t = clonedTasks.find((task: any) => task.id === id);
          return t ? t.name : 'Unknown Task';
        });
        const templateList = dependencies.templateAdditionalDependencies.map((id) => {
          const t = clonedTasks.find((task: any) => task.id === id);
          return t ? t.name : 'Unknown Task';
        });
        diffs.push({
          field: 'Preceding Tasks',
          sepValue: taskList,
          templateValue: templateList
        });
      }

      const followDependencies = getTaskAdditionalDependencies(taskFollowingIds, templateFollowingIds);
      if (followDependencies.isDifferent) {
        const taskList = followDependencies.taskAdditionalDependencies.map((id) => {
          const t = clonedTasks.find((task: any) => task.id === id);
          return t ? t.name : 'Unknown Task';
        });
        const templateList = followDependencies.templateAdditionalDependencies.map((id) => {
          const t = clonedTasks.find((task: any) => task.id === id);
          return t ? t.name : 'Unknown Task';
        });
        diffs.push({
          field: 'Forwarding Tasks',
          sepValue: taskList,
          templateValue: templateList
        });
      }
      if (diffs.length > 0) {
        rows.push({
          sepName: sepObj.name,
          taskName: task.name,
          differences: diffs,
        });
      }
    });
  });

  // Group rows by SEP name
  const groupedRows: { [sepName: string]: typeof rows } = {};
  rows.forEach(row => {
    if (!groupedRows[row.sepName]) groupedRows[row.sepName] = [];
    groupedRows[row.sepName].push(row);
  });

  return (
    <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 3, borderRadius: 2 }}>
      <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.h6, mb: 2, px: 2, pt: 2, color: theme.palette.primary.main }}>
        SEP Task vs Template Task Differences
      </Typography>
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow sx={{ background: theme.palette.primary.main }}>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.base, width: '10%' }}>SEP Name</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.base, width: '25%' }}>Task Name</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.base, width: '15%' }}>Field</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.base, width: '25%' }}>Task Value</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.base, width: '25%' }}>Template Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ fontSize: typographyTokens.fontSizes.base, color: '#888', py: 3 }}>
                No differences found. All SEP tasks match their templates.
              </TableCell>
            </TableRow>
          ) : (
            Object.entries(groupedRows).flatMap(([sepName, sepRows]) => {
              const sepRowCount = sepRows.reduce((acc, r) => acc + r.differences.length, 0);
              let rendered = 0;
              return sepRows.flatMap((row, idx) => (
                row.differences.map((diff, dIdx) => {
                  rendered++;
                  return (
                    <TableRow key={sepName + '-' + idx + '-' + dIdx} sx={{ background: dIdx % 2 === 0 ? '#f5f8fc' : '#fff' }}>
                      {rendered === 1 ? (
                        <TableCell rowSpan={sepRowCount} sx={{ fontWeight: typographyTokens.fontWeights.bold, background: '#e3f2fd', color: theme.palette.primary.main, fontSize: typographyTokens.fontSizes.base, verticalAlign: 'middle' }}>{sepName}</TableCell>
                      ) : null}
                      <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, color: theme.palette.primary.main }}>{row.taskName}</TableCell>
                      <TableCell sx={{ color: theme.palette.primary.main, fontWeight: typographyTokens.fontWeights.bold }}>{diff.field}</TableCell>
                      <TableCell sx={{ color: '#d32f2f', fontWeight: typographyTokens.fontWeights.medium, width: '25%', wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
                        {Array.isArray(diff.sepValue) ? (
                          <ul style={{ margin: 0, paddingLeft: '18px' }}>
                            {diff.sepValue.map((item: string, idx: number) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        ) : diff.sepValue}
                      </TableCell>
                      <TableCell sx={{ color: '#388e3c', fontWeight: typographyTokens.fontWeights.medium, width: '25%' }}>
                        {Array.isArray(diff.templateValue) ? (
                          <ul style={{ margin: 0, paddingLeft: '18px' }}>
                            {diff.templateValue.map((item: string, idx: number) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        ) : diff.templateValue}
                      </TableCell>
                    </TableRow>
                  );
                })
              ))
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default WorkflowValidationTable;